import React from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

// Compact strip showing each ally's current HP. Sits under the player's top bar
// so they can see at a glance who's hurting without opening initiative.
// Hides the current player (their own HP is in the bottom bar) and excludes
// hidden / mirror-image tokens.
export default function PartyHpStrip({ myTokenId, activeTokenId }) {
  const { tokens } = useCampaign();
  const allies = Object.values(tokens).filter((t) =>
    (t.type === 'player' || t.type === 'friendlyNPC')
    && t.id !== myTokenId
    && !t.hidden
    && !t.mirrorDuplicate
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (allies.length === 0) return null;
  return (
    <div style={styles.row}>
      {allies.map((t) => {
        const max = Math.max(1, t.maxHp || 10);
        const pct = Math.max(0, Math.min(1, t.hp / max));
        const hpColor =
          t.hp <= 0       ? '#4b5563'
          : pct > 0.5     ? '#4ade80'
          : pct > 0.35    ? '#fb923c'
          : pct > 0.15    ? '#facc15'
          : '#f87171';
        const isActive = t.id === activeTokenId;
        const firstName = (t.name || '').split(/\s+/)[0] || '?';
        return (
          <div
            key={t.id}
            style={{ ...styles.member, ...(isActive ? styles.activeMember : {}) }}
            title={`${t.name} — ${t.hp}/${max} HP`}
          >
            <span style={{ ...styles.dot, background: t.colour || '#888' }} />
            <span style={styles.name}>{firstName}</span>
            <div style={styles.hpBar}>
              <div style={{ ...styles.hpFill, width: `${pct * 100}%`, background: hpColor }} />
              <span style={styles.hpNum}>{t.hp}/{max}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  row: {
    display: 'flex',
    gap: 6,
    padding: '4px 8px',
    background: 'rgba(10,15,30,0.92)',
    borderBottom: '1px solid #4a3d28',
    overflowX: 'auto',
    flexShrink: 0,
    scrollbarWidth: 'thin',
  },
  member: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    padding: '2px 6px 2px 4px',
    borderRadius: 4,
    border: '1px solid transparent',
    background: 'rgba(20,32,56,0.7)',
  },
  activeMember: {
    borderColor: '#facc15',
    boxShadow: '0 0 6px rgba(250,204,21,0.4)',
  },
  dot: {
    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
    border: '1px solid rgba(0,0,0,0.4)',
  },
  name: {
    fontSize: 11, fontWeight: 700, color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  hpBar: {
    position: 'relative',
    width: 70, height: 12,
    background: '#0e1528',
    border: '1px solid #4a3d28',
    borderRadius: 2,
    overflow: 'hidden',
  },
  hpFill: {
    height: '100%',
    transition: 'width 0.25s ease, background 0.2s ease',
  },
  hpNum: {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 800, color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.9)',
  },
};
